import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';

/**
 * LiveOps WS surface:
 * - `LIVEOPS_EVENT` — client/host → server dedicated funnel milestone.
 *
 * This is the single source of truth for the LiveOps data contract. The
 * realtime-api ingest (server) and the admin-dashboard consumption both agree
 * on the funnel-stage and error-category vocabularies defined here, so the two
 * sides can never drift apart again.
 *
 * The server NEVER trusts identity from the wire: campaignId / sessionId /
 * clientType are resolved server-side from the authenticated connection. The
 * client only states WHAT happened and WHEN.
 */
export enum LiveOpsMessageType {
  LIVEOPS_EVENT = 'LIVEOPS_EVENT',
}

/** The only events a client/host connection is allowed to emit. */
export enum LiveOpsClientEventName {
  PAGE_LOADED = 'page_loaded',
  STREAM_RENDERED = 'stream_rendered',
  FIRST_INPUT = 'first_input',
}

/**
 * Sanity cap for a client-reported connection duration. Durations beyond the
 * connection-time histogram max still bucket into the top bin; this bound only
 * rejects absurd/malicious values that would corrupt percentiles.
 */
export const MAX_CONNECTION_DURATION_MS = 600_000; // 10 minutes

/**
 * Client payload. Identity fields are intentionally absent — the server stamps
 * campaignId / sessionId / clientType from the authenticated connection.
 *
 * Numeric fields are bounded so a buggy/malicious client cannot poison the
 * connection-time histogram: durations must be finite, non-negative, and within
 * a sane cap.
 */
export const liveOpsEventPayloadSchema = z.object({
  eventName: z.nativeEnum(LiveOpsClientEventName),
  occurredAt: z.number().finite().nonnegative(), // client epoch ms
  connectionDurationMs: z
    .number()
    .finite()
    .nonnegative()
    .max(MAX_CONNECTION_DURATION_MS)
    .optional(), // only meaningful for stream_rendered
});

const liveOpsEventMessageSchema = baseMessageSchema
  .extend({ type: z.literal(LiveOpsMessageType.LIVEOPS_EVENT) })
  .merge(liveOpsEventPayloadSchema);

export const liveOpsMessageUnion = liveOpsEventMessageSchema;

export type LiveOpsEventPayload = z.infer<typeof liveOpsEventPayloadSchema>;
export type LiveOpsMessage = z.infer<typeof liveOpsMessageUnion>;

export const generators = {
  liveOpsEvent: (payload: LiveOpsEventPayload) => ({
    id: createMessageId(),
    type: LiveOpsMessageType.LIVEOPS_EVENT as const,
    timestamp: Date.now(),
    ...payload,
  }),
};

export const validators = {
  liveOpsEvent: (message: unknown) => {
    const result = liveOpsMessageUnion.safeParse(message);
    return result.success ? result.data : null;
  },
};

/** Canonical funnel order — consumed verbatim by server FUNNEL_STAGES and dashboard FUNNEL_STAGES. */
export const LIVEOPS_FUNNEL_STAGES = [
  'page_loaded',
  'matchmaking_started',
  'matchmaking_succeeded',
  'broadcast_joined',
  'player_joined_broadcast',
  'stream_rendered',
  'first_input',
] as const;
export type LiveOpsFunnelStage = (typeof LIVEOPS_FUNNEL_STAGES)[number];

/** Canonical error-breakdown keys — consumed verbatim by server + dashboard. */
export const LIVEOPS_ERROR_KEYS = [
  'host_timeout',
  'no_available_hosts',
  'server_disconnect',
  'user_closed_browser',
  'connection_lost',
  'user_cancelled',
  'other',
] as const;
export type LiveOpsErrorKey = (typeof LIVEOPS_ERROR_KEYS)[number];
